import { useEffect, useRef, useState } from "react";
import { Loader2, Plus, Trash2, GripVertical, Images, Clock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/admin/ui";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ANIMATIONS = [
  { value: "fade", label: "Suave (esmaecer)" },
  { value: "slide", label: "Lateral (deslizar)" },
  { value: "zoom", label: "Zoom (aproximar)" },
];

export default function CarouselPage() {
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const fileInput = useRef(null);

  const load = () => api.get("/carousel").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/carousel/images", fd);
      toast.success("Imagem adicionada ao carrossel!");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (img) => {
    try {
      await api.delete(`/carousel/images/${img.id}`);
      toast.success("Imagem removida");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const reorder = async (from, to) => {
    if (from === to) return;
    const ids = data.images.map((i) => i.id);
    const [moved] = ids.splice(from, 1);
    ids.splice(to, 0, moved);
    setData({ ...data, images: ids.map((id) => data.images.find((i) => i.id === id)) });
    try {
      await api.put("/carousel/order", { ids });
      toast.success("Ordem atualizada");
    } catch (err) {
      toast.error(formatApiError(err));
      load();
    }
  };

  const saveSettings = async (patch) => {
    const next = { interval: data.interval, animation: data.animation, ...patch };
    setData({ ...data, ...next });
    setSavingSettings(true);
    try {
      await api.put("/carousel/settings", next);
      toast.success("Configuração salva — já vale no site");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingSettings(false);
    }
  };

  if (!data) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 size={28} className="animate-spin text-[#FFB800]" />
      </div>
    );
  }

  return (
    <div data-testid="carousel-page">
      <PageHeader
        title="Carrossel do Sobre"
        sub={`${data.images.length}/10 imagens — arraste uma sobre a outra para mudar a ordem`}
        testId="carousel-header"
      >
        <input ref={fileInput} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="carousel-file-input" />
        <button
          onClick={() => fileInput.current?.click()}
          disabled={uploading || data.images.length >= 10}
          data-testid="carousel-upload-button"
          className="flex items-center gap-2 rounded-full bg-[#FFB800] px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-black transition-transform hover:scale-105 disabled:opacity-50"
        >
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} strokeWidth={3} />}
          {uploading ? "Enviando..." : "Adicionar Imagem"}
        </button>
      </PageHeader>

      <div className="mb-6 grid gap-4 rounded-2xl border border-white/5 bg-[#11141A] p-6 sm:grid-cols-2" data-testid="carousel-settings">
        <div>
          <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-400">
            <Clock size={14} className="text-[#FFB800]" /> Tempo entre fotos (segundos)
          </label>
          <div className="mt-3 flex items-center gap-4">
            <input
              type="range"
              min={2}
              max={30}
              value={data.interval}
              onChange={(e) => setData({ ...data, interval: Number(e.target.value) })}
              onMouseUp={() => saveSettings({ interval: data.interval })}
              onTouchEnd={() => saveSettings({ interval: data.interval })}
              data-testid="carousel-interval-slider"
              className="flex-1 accent-[#FFB800]"
            />
            <span className="w-14 rounded-lg border border-white/10 bg-[#0A0B0D] px-3 py-1.5 text-center font-display text-lg font-bold text-[#FFB800]" data-testid="carousel-interval-value">
              {data.interval}s
            </span>
          </div>
        </div>
        <div>
          <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-400">
            <Sparkles size={14} className="text-[#FFB800]" /> Tipo de animação
          </label>
          <div className="mt-3 flex gap-2">
            {ANIMATIONS.map((a) => (
              <button
                key={a.value}
                onClick={() => saveSettings({ animation: a.value })}
                data-testid={`carousel-animation-${a.value}`}
                className={`flex-1 rounded-xl border px-3 py-2.5 text-xs font-bold uppercase tracking-wide transition-colors ${
                  data.animation === a.value
                    ? "border-[#FFB800] bg-[#FFB800]/10 text-[#FFB800]"
                    : "border-white/10 text-zinc-400 hover:border-white/25 hover:text-white"
                }`}
              >
                {a.label}
              </button>
            ))}
          </div>
        </div>
        {savingSettings && <p className="text-xs text-zinc-500 sm:col-span-2">Salvando...</p>}
      </div>

      {data.images.length === 0 ? (
        <div className="flex flex-col items-center gap-4 rounded-2xl border border-dashed border-white/10 bg-[#11141A] py-20 text-center" data-testid="carousel-empty">
          <Images size={40} className="text-zinc-600" />
          <p className="max-w-sm text-sm text-zinc-500">Nenhuma imagem ainda. Adicione até 10 fotos dos seus trabalhos — elas aparecem na seção "Sobre" do site.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5" data-testid="carousel-grid">
          {data.images.map((img, i) => (
            <div
              key={img.id}
              draggable
              onDragStart={() => setDragIndex(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => { reorder(dragIndex, i); setDragIndex(null); }}
              onDragEnd={() => setDragIndex(null)}
              data-testid={`carousel-item-${i}`}
              className={`group relative cursor-grab overflow-hidden rounded-2xl border-2 bg-[#11141A] transition-[transform,border-color,opacity] duration-200 active:cursor-grabbing ${
                dragIndex === i ? "scale-95 border-[#FFB800] opacity-60" : "border-white/5 hover:border-[#FFB800]/50"
              }`}
            >
              <img src={`${BACKEND}${img.url}`} alt={`Foto ${i + 1}`} className="aspect-[3/4] w-full object-cover" draggable={false} />
              <div className="absolute inset-x-0 top-0 flex items-center justify-between bg-gradient-to-b from-black/70 to-transparent p-2.5">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#FFB800] font-display text-xs font-bold text-black">
                  {i + 1}
                </span>
                <button
                  onClick={() => remove(img)}
                  data-testid={`carousel-delete-${i}`}
                  className="flex h-7 w-7 items-center justify-center rounded-full bg-red-500/90 text-white opacity-0 transition-opacity group-hover:opacity-100"
                  aria-label="Remover imagem"
                >
                  <Trash2 size={13} />
                </button>
              </div>
              <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full bg-black/60 px-2.5 py-1 text-[10px] font-semibold text-zinc-300 opacity-0 transition-opacity group-hover:opacity-100">
                <GripVertical size={11} /> arraste p/ reordenar
              </div>
            </div>
          ))}
          {data.images.length < 10 && (
            <button
              onClick={() => fileInput.current?.click()}
              data-testid="carousel-add-slot"
              className="flex aspect-[3/4] flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-white/10 text-zinc-600 transition-colors hover:border-[#FFB800]/50 hover:text-[#FFB800]"
            >
              <Plus size={26} />
              <span className="text-xs font-semibold">Adicionar</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
